'use client';

import * as Toast from '@radix-ui/react-toast';
import { X } from 'lucide-react';
import { toast, useToasts, type ToastVariant } from '@/lib/toast';
import { cn } from '@/lib/utils';

const VARIANT_LABEL: Record<ToastVariant, string> = {
  success: 'Done',
  info: 'Note',
  error: 'Error',
};

// Map each variant to a CSS color for the eyebrow + left rail. Keeps the
// rest of the toast paper-neutral so the message reads cleanly.
const VARIANT_COLOR: Record<ToastVariant, string> = {
  success: 'hsl(var(--foreground))',
  info: 'hsl(var(--section-schedule))',
  error: 'hsl(var(--priority-urgent))',
};

// Toasts anchor top-right just under the sticky tab strip (top ~14h header
// + ~50px tabs ≈ 7rem). Stack grows downward; Radix swipe-to-dismiss is
// enabled in the same direction as the anchor side.
export function Toaster() {
  const toasts = useToasts();

  return (
    <Toast.Provider duration={4500} swipeDirection="right">
      {toasts.map((t) => (
        <Toast.Root
          key={t.id}
          duration={t.duration ?? 4500}
          onOpenChange={(open) => {
            if (!open) toast.dismiss(t.id);
          }}
          className={cn(
            'group pointer-events-auto relative flex w-[22rem] max-w-[calc(100vw-2rem)] items-start gap-3 overflow-hidden rounded-xl border border-hairline bg-surface/70 p-4 pl-5 shadow-lg backdrop-blur-sm',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=open]:slide-in-from-top-2 data-[state=open]:fade-in',
            'data-[state=closed]:fade-out data-[state=closed]:slide-out-to-right-full',
            'data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)]',
            'data-[swipe=cancel]:translate-x-0 data-[swipe=cancel]:transition-transform',
            'data-[swipe=end]:translate-x-[var(--radix-toast-swipe-end-x)] data-[swipe=end]:animate-out data-[swipe=end]:fade-out',
          )}
        >
          {/* Left accent rail in the variant color. */}
          <span
            aria-hidden
            className="absolute inset-y-0 left-0 w-[3px]"
            style={{ background: VARIANT_COLOR[t.variant] }}
          />

          <div className="min-w-0 flex-1 space-y-0.5">
            <Toast.Title
              className="eyebrow eyebrow-strong"
              style={{ color: VARIANT_COLOR[t.variant] }}
            >
              {VARIANT_LABEL[t.variant]}
            </Toast.Title>
            <p className="truncate text-sm font-medium text-foreground">{t.title}</p>
            {t.description && (
              <Toast.Description className="text-xs text-muted-foreground">
                {t.description}
              </Toast.Description>
            )}
          </div>

          <div className="flex shrink-0 items-center gap-3 self-center">
            {t.actionLabel && t.onAction && (
              <Toast.Action
                altText={t.actionLabel}
                onClick={() => {
                  t.onAction?.();
                  toast.dismiss(t.id);
                }}
                className="font-mono text-[11px] uppercase tracking-eyebrow text-accent transition duration-fast ease-out-quint hover:brightness-110"
              >
                {t.actionLabel}
              </Toast.Action>
            )}
            <Toast.Close
              aria-label="Dismiss"
              className="rounded-lg p-1.5 text-muted-foreground transition duration-fast ease-out-quint hover:bg-surface-muted hover:text-foreground"
            >
              <X className="h-3.5 w-3.5" strokeWidth={2} />
            </Toast.Close>
          </div>
        </Toast.Root>
      ))}

      <Toast.Viewport
        className={cn(
          'fixed top-28 right-5 z-50 flex w-[22rem] max-w-[calc(100vw-2rem)] flex-col gap-2',
          'outline-none sm:right-10',
        )}
      />
    </Toast.Provider>
  );
}
